import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  Plus, X, Edit, Trash2, Send, Clock, AlertCircle, CheckCircle2,
  Facebook, Instagram, Twitter, Linkedin
} from 'lucide-react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const SocialMediaCalendar = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // month, week, list
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      const matchStatus = filterStatus === 'all' || post.status === filterStatus;
      const matchPlatform = filterPlatform === 'all' || post.platforms.includes(filterPlatform);
      return matchStatus && matchPlatform;
    });
  }, [posts, filterStatus, filterPlatform]);

  useEffect(() => {
    fetchPosts();
  }, [currentYear, currentMonth, view]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const endpoint = view === 'list' 
        ? '/api/social-media/posts?limit=100' 
        : `/api/social-media/posts?month=${currentMonth + 1}&year=${currentYear}`;
      const res = await api.get(endpoint);
      const postsArray = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      const mapped = postsArray.map(post => ({
        ...post,
        date: post.scheduled_at || post.published_at || post.created_at
      }));
      setPosts(mapped);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      // Mock data for visual demonstration if API fails
      setPosts([
        { id: 1, date: new Date(currentYear, currentMonth, 5).toISOString(), content: 'Summer special menu!', status: 'published', platforms: ['facebook', 'instagram'] },
        { id: 2, date: new Date(currentYear, currentMonth, 12).toISOString(), content: 'Join us for happy hour.', status: 'scheduled', platforms: ['facebook'] },
        { id: 3, date: new Date(currentYear, currentMonth, 15).toISOString(), content: 'New chef interview.', status: 'draft', platforms: ['instagram'] },
        { id: 4, date: new Date(currentYear, currentMonth, 18).toISOString(), content: 'Oops something went wrong', status: 'failed', platforms: ['facebook'] },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const prevMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const calendarCells = useMemo(() => {
    const cells = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      cells.push({ day: null, date: null });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentYear, currentMonth, i);
      cells.push({ day: i, date });
    }
    return cells;
  }, [currentYear, currentMonth, daysInMonth, firstDayOfMonth]);

  const getPostsForDate = (date) => {
    if (!date) return [];
    return posts.filter(post => {
      const postDate = new Date(post.date);
      return postDate.getDate() === date.getDate() &&
             postDate.getMonth() === date.getMonth() &&
             postDate.getFullYear() === date.getFullYear();
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'published': return 'bg-green-500';
      case 'scheduled': return 'bg-blue-500';
      case 'draft': return 'bg-gray-400';
      case 'failed': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const renderPlatformIcon = (platform, size=14) => {
    switch(platform) {
      case 'facebook': return <Facebook size={size} className="text-blue-600" />;
      case 'instagram': return <Instagram size={size} className="text-pink-600" />;
      case 'twitter': return <Twitter size={size} className="text-blue-400" />;
      case 'linkedin': return <Linkedin size={size} className="text-blue-700" />;
      default: return null;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen bg-gray-50/50">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">Content Calendar</h1>
          <p className="text-gray-500 mt-1">Manage and schedule your social media presence</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/admin/social-media/compose')}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all font-medium"
          >
            <Plus size={18} />
            <span>New Post</span>
          </button>
        </div>
      </div>

      {/* Calendar Controls */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
        {view !== 'list' ? (
          <div className="flex items-center gap-2">
            <button onClick={goToToday} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              Today
            </button>
            <div className="flex items-center bg-gray-100 rounded-lg p-1 ml-2">
              <button onClick={prevMonth} className="p-1.5 text-gray-600 hover:bg-white rounded-md transition-colors shadow-sm-hover">
                <ChevronLeft size={18} />
              </button>
              <button onClick={nextMonth} className="p-1.5 text-gray-600 hover:bg-white rounded-md transition-colors shadow-sm-hover">
                <ChevronRight size={18} />
              </button>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 ml-4 min-w-[160px]">
              {currentDate.toLocaleString('default', { month: 'long' })} {currentYear}
            </h2>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-800">All Posts List</h2>
          </div>
        )}
        
        <div className="flex bg-gray-100 p-1 rounded-lg">
          <button 
            onClick={() => setView('month')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${view === 'month' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Month
          </button>
          <button 
            onClick={() => setView('week')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${view === 'week' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Week
          </button>
          <button 
            onClick={() => setView('list')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${view === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            List
          </button>
        </div>
      </div>

      {/* Calendar Grid vs List View */}
      {view === 'list' ? (
        <div className="space-y-6">
          {/* List View Filters */}
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {['all', 'published', 'scheduled', 'draft', 'failed'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all capitalize ${
                    filterStatus === status 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-sm hover:shadow-lg' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              {['all', 'facebook', 'instagram'].map(platform => (
                <button
                  key={platform}
                  onClick={() => setFilterPlatform(platform)}
                  className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all capitalize flex items-center gap-1.5 ${
                    filterPlatform === platform 
                      ? 'bg-gray-900 text-white shadow-sm' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {platform === 'all' ? 'All Platforms' : platform}
                </button>
              ))}
            </div>
          </div>

          {/* List Content */}
          {loading ? (
            <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-100 flex justify-center items-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="bg-white rounded-2xl p-16 shadow-sm border border-gray-100 text-center">
              <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CalendarIcon size={24} className="text-gray-400" />
              </div>
              <h4 className="text-gray-900 font-bold text-lg mb-2">No posts found</h4>
              <p className="text-gray-500 text-sm">No items match your selected filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map(post => (
                <div key={post.id} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-1.5">
                        {post.platforms.map(p => (
                          <div key={p} className="p-1.5 bg-gray-50 rounded-md">
                            {renderPlatformIcon(p, 16)}
                          </div>
                        ))}
                      </div>
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full capitalize border
                        ${post.status === 'published' ? 'bg-green-50 border-green-100 text-green-700' : ''}
                        ${post.status === 'scheduled' ? 'bg-blue-50 border-blue-100 text-blue-700' : ''}
                        ${post.status === 'draft' ? 'bg-gray-50 border-gray-200 text-gray-700' : ''}
                        ${post.status === 'failed' ? 'bg-red-50 border-red-100 text-red-700' : ''}
                      `}>
                        {post.status}
                      </span>
                    </div>
                    {post.media_urls?.[0] && (
                      <div className="aspect-video w-full rounded-xl overflow-hidden mb-3 border border-gray-100">
                        <img src={post.media_urls[0]} alt="preview" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <p className="text-gray-800 text-sm font-medium line-clamp-3 mb-4 leading-relaxed">{post.content}</p>
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-gray-50 mt-4">
                    <div className="flex items-center text-xs text-gray-500 gap-1.5">
                      <Clock size={14} />
                      {new Date(post.date).toLocaleDateString()} {new Date(post.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => navigate(`/admin/social-media/compose?edit=${post.id}`)} 
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all" 
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Calendar Grid */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Days Header */}
            <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50/50">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="py-3 text-center text-sm font-semibold text-gray-500">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Grid Cells */}
            <div className="grid grid-cols-7 auto-rows-[120px] md:auto-rows-[160px] relative">
              {loading && (
                <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              )}
              
              {calendarCells.map((cell, idx) => {
                const dayPosts = getPostsForDate(cell.date);
                const isToday = cell.date?.getDate() === new Date().getDate() && 
                                cell.date?.getMonth() === new Date().getMonth() && 
                                cell.date?.getFullYear() === new Date().getFullYear();

                return (
                  <div 
                    key={idx} 
                    onClick={() => cell.date && setSelectedDay(cell.date)}
                    className={`border-b border-r border-gray-100 p-2 transition-colors cursor-pointer
                      ${!cell.day ? 'bg-gray-50/50' : 'hover:bg-blue-50/30'}
                      ${(idx + 1) % 7 === 0 ? 'border-r-0' : ''}
                    `}
                  >
                    {cell.day && (
                      <>
                        <div className="flex justify-between items-start mb-2">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium
                            ${isToday ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' : 'text-gray-700'}
                          `}>
                            {cell.day}
                          </span>
                        </div>
                        
                        <div className="space-y-1.5 max-h-[85px] md:max-h-[120px] overflow-hidden">
                          {dayPosts.map((post, i) => (
                            <div 
                              key={post.id} 
                              onClick={(e) => { e.stopPropagation(); navigate(`/admin/social-media/compose?edit=${post.id}`); }}
                              className={`text-xs p-1.5 rounded-lg border flex items-center gap-1.5 truncate hover:shadow-sm transition-all
                                ${post.status === 'published' ? 'bg-green-50 border-green-100 text-green-700' : ''}
                                ${post.status === 'scheduled' ? 'bg-blue-50 border-blue-100 text-blue-700' : ''}
                                ${post.status === 'draft' ? 'bg-gray-50 border-gray-200 text-gray-700' : ''}
                                ${post.status === 'failed' ? 'bg-red-50 border-red-100 text-red-700' : ''}
                              `}
                            >
                              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getStatusColor(post.status)}`} />
                              <span className="truncate flex-1 font-medium">{post.content}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-6 mt-6 px-4">
            {[
              { label: 'Published', color: 'bg-green-500' },
              { label: 'Scheduled', color: 'bg-blue-500' },
              { label: 'Draft', color: 'bg-gray-400' },
              { label: 'Failed', color: 'bg-red-500' }
            ].map(status => (
              <div key={status.label} className="flex items-center gap-2 text-sm text-gray-600">
                <div className={`w-2.5 h-2.5 rounded-full ${status.color}`}></div>
                {status.label}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Day Detail Slide-out / Modal */}
      {selectedDay && (
        <>
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={() => setSelectedDay(null)}></div>
          <div className="fixed top-0 right-0 h-full w-full md:w-[450px] bg-white shadow-2xl z-50 transform transition-transform border-l border-gray-100 flex flex-col">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedDay.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric' })}
                </h3>
                <p className="text-sm text-gray-500 mt-1">{getPostsForDate(selectedDay).length} posts scheduled</p>
              </div>
              <button onClick={() => setSelectedDay(null)} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors text-gray-600">
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50/50">
              {getPostsForDate(selectedDay).length === 0 ? (
                <div className="text-center py-12">
                  <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CalendarIcon size={24} className="text-gray-400" />
                  </div>
                  <h4 className="text-gray-900 font-medium mb-2">No posts scheduled</h4>
                  <p className="text-gray-500 text-sm mb-6">You don't have any content planned for this day.</p>
                  <button onClick={() => navigate(`/admin/social-media/compose?date=${selectedDay.toISOString()}`)} className="text-blue-600 font-medium hover:text-blue-700">
                    + Schedule a post
                  </button>
                </div>
              ) : (
                getPostsForDate(selectedDay).map(post => (
                  <div key={post.id} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        {post.platforms.map(p => (
                          <div key={p} className="p-1.5 bg-gray-50 rounded-md">
                            {renderPlatformIcon(p, 16)}
                          </div>
                        ))}
                      </div>
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full capitalize border
                        ${post.status === 'published' ? 'bg-green-50 border-green-100 text-green-700' : ''}
                        ${post.status === 'scheduled' ? 'bg-blue-50 border-blue-100 text-blue-700' : ''}
                        ${post.status === 'draft' ? 'bg-gray-50 border-gray-200 text-gray-700' : ''}
                        ${post.status === 'failed' ? 'bg-red-50 border-red-100 text-red-700' : ''}
                      `}>
                        {post.status}
                      </span>
                    </div>
                    <p className="text-gray-800 text-sm line-clamp-3 mb-4">{post.content}</p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                      <div className="flex items-center text-xs text-gray-500 gap-1.5">
                        <Clock size={14} />
                        {new Date(post.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => navigate(`/admin/social-media/compose?edit=${post.id}`)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="Edit">
                          <Edit size={16} />
                        </button>
                        <button className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div className="p-6 border-t border-gray-100 bg-white">
              <button 
                onClick={() => navigate(`/admin/social-media/compose?date=${selectedDay.toISOString()}`)}
                className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white px-4 py-3 rounded-xl hover:bg-gray-800 transition-colors font-medium shadow-md shadow-gray-900/10"
              >
                <Plus size={18} />
                Create Post for this Day
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SocialMediaCalendar;
